import { useEffect } from 'react'
import { useThemeOrchestratorStore } from '@/stores/themeOrchestratorStore'
import { useTaskStore } from '@/stores/taskStore'
import { useSettingsStore, applyTheme } from '@/stores/settingsStore'
import { useWeatherStore } from '@/stores/weatherStore'

export function useThemeOrchestrator() {
  const activeOverlay = useThemeOrchestratorStore((s) => s.activeOverlay)
  const resolveOverlay = useThemeOrchestratorStore((s) => s.resolveOverlay)
  const initialize = useThemeOrchestratorStore((s) => s.initialize)

  // 날씨 기반 일출/일몰 타이머용 구독
  const weatherData = useWeatherStore((s) => s.weatherData)
  const weatherEnabled = useSettingsStore((s) => s.settings.weatherThemeEnabled)

  // Initialize on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  // Re-resolve when tasks change
  useEffect(() => {
    const unsub = useTaskStore.subscribe(() => {
      resolveOverlay()
    })
    return unsub
  }, [resolveOverlay])

  // Re-resolve when relevant settings change + 날씨 테마 토글 시 라이트/다크 즉시 반영
  useEffect(() => {
    const unsub = useSettingsStore.subscribe((state, prevState) => {
      if (
        state.settings.survivalModeEnabled !== prevState.settings.survivalModeEnabled ||
        state.settings.timeCapsuleEnabled !== prevState.settings.timeCapsuleEnabled ||
        state.settings.gamificationEnabled !== prevState.settings.gamificationEnabled ||
        state.settings.weatherThemeEnabled !== prevState.settings.weatherThemeEnabled
      ) {
        resolveOverlay()
      }
      // 날씨 테마 토글 시 라이트/다크 모드도 즉시 재적용
      if (state.settings.weatherThemeEnabled !== prevState.settings.weatherThemeEnabled) {
        applyTheme(state.settings.theme)
      }
    })
    return unsub
  }, [resolveOverlay])

  // Re-resolve when weather data changes + 일출/일몰 변경 시 테마 재적용
  useEffect(() => {
    const unsub = useWeatherStore.subscribe((state, prevState) => {
      if (state.weatherData?.condition !== prevState.weatherData?.condition) {
        resolveOverlay()
      }
      // 일출/일몰 데이터 변경 시 라이트/다크 재적용
      if (
        state.weatherData?.sunrise !== prevState.weatherData?.sunrise ||
        state.weatherData?.sunset !== prevState.weatherData?.sunset
      ) {
        applyTheme(useSettingsStore.getState().settings.theme)
      }
    })
    return unsub
  }, [resolveOverlay])

  // 일출/일몰 정확한 시각에 테마 자동 전환 타이머
  useEffect(() => {
    if (!weatherEnabled) return
    if (!weatherData?.sunrise || !weatherData?.sunset) return

    const now = Date.now()
    const sunriseMs = new Date(weatherData.sunrise).getTime()
    const sunsetMs = new Date(weatherData.sunset).getTime()
    const timers: ReturnType<typeof setTimeout>[] = []

    // 일출 시각에 라이트 모드 전환
    if (sunriseMs > now) {
      timers.push(setTimeout(() => {
        applyTheme(useSettingsStore.getState().settings.theme)
      }, sunriseMs - now))
    }

    // 일몰 시각에 다크 모드 전환
    if (sunsetMs > now) {
      timers.push(setTimeout(() => {
        applyTheme(useSettingsStore.getState().settings.theme)
      }, sunsetMs - now))
    }

    return () => timers.forEach(clearTimeout)
  }, [weatherData?.sunrise, weatherData?.sunset, weatherEnabled])

  // Apply overlay to DOM
  useEffect(() => {
    const root = document.documentElement
    if (activeOverlay === 'none') {
      root.removeAttribute('data-theme-overlay')
    } else {
      root.setAttribute('data-theme-overlay', activeOverlay)
    }

    return () => {
      root.removeAttribute('data-theme-overlay')
    }
  }, [activeOverlay])
}
